var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useAppDispatch, useAppSelector } from 'root/src/client/logic/helpers/useStore';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { ActionStrategyId } from '@daredrop-com/rewards-api-contract';
import { giveawayAnalyticsEvents, giveawayEndpointIds, } from 'root/src/shared/giveaways/giveaways.constants';
import { authApiDomain } from 'root/src/shared/constants/authApi';
import { getCookie } from 'root/src/client/v2/browser/helpers/cookies';
import { apiGatewayEndpoint } from 'root/src/shared/constants/aws';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import creatorDataHof from 'root/src/client/logic/creatorData/util/creatorDataHof';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { AuthBroadcastEvent, getAuthBroadcastChannel, } from 'root/src/client/web/auth/fixtures/auth.fixtures';
const entryEventsMap = new Map([
    [ActionStrategyId.EnterGiveawayTwitch, giveawayAnalyticsEvents.SUBMIT_ENTRY_TWITCH],
    [ActionStrategyId.EnterGiveawayYoutube, giveawayAnalyticsEvents.SUBMIT_ENTRY_YOUTUBE],
    [ActionStrategyId.EnterGiveawayTwitchSubscription, giveawayAnalyticsEvents.SUBMIT_ENTRY_TWITCH_SUBSCRIPTION],
]);
const entryEventsSuccessMap = new Map([
    [ActionStrategyId.EnterGiveawayTwitch, giveawayAnalyticsEvents.SUBMIT_ENTRY_TWITCH_SUCCESS],
    [ActionStrategyId.EnterGiveawayYoutube, giveawayAnalyticsEvents.SUBMIT_ENTRY_YOUTUBE_SUCCESS],
    [ActionStrategyId.EnterGiveawayTwitchSubscription, giveawayAnalyticsEvents.SUBMIT_ENTRY_TWITCH_SUBSCRIPTION_SUCCESS],
]);
export const useGiveawayApi = () => {
    const { userId } = useUserId();
    const dispatch = useAppDispatch();
    const targetUserId = useAppSelector(creatorDataHof('userId'));
    const createEntry = (params) => __awaiter(void 0, void 0, void 0, function* () {
        const eventName = entryEventsMap.get(params.strategyId);
        if (eventName) {
            analyticsSendItem(eventName, userId);
        }
        const result = yield dispatch(apiRequest(giveawayEndpointIds.CREATE_GIVEAWAY_ENTRY, {
            actionId: params.actionId,
            giveawayId: params.giveawayId,
        }));
        if (!result.error) {
            const eventSuccessName = entryEventsSuccessMap.get(params.strategyId);
            if (eventSuccessName) {
                analyticsSendItem(eventSuccessName, userId);
            }
        }
        return result;
    });
    const redeemVoucher = (params) => __awaiter(void 0, void 0, void 0, function* () {
        analyticsSendItem(giveawayAnalyticsEvents.REDEEM_VOUCHER, userId);
        return dispatch(apiRequest(giveawayEndpointIds.REDEEM_REWARD, {
            rewardId: params.rewardId,
            voucherId: params.voucherId,
        }));
    });
    const openSteamPopup = (event) => {
        const width = 540;
        const height = 700;
        const left = event.screenX - (width / 2);
        const top = event.screenY - (height);
        const popup = window.open('about:blank', 'steam-auth', `width=${width},height=${height},left=${left},top=${top}`);
        if (!popup) {
            throw new Error('Could not open popup, please check your browser settings');
        }
        return popup;
    };
    const submitSteamEntry = (popup, params) => __awaiter(void 0, void 0, void 0, function* () {
        analyticsSendItem(giveawayAnalyticsEvents.SUBMIT_STEAM_ENTRY, userId);
        const url = new URL(`https://${authApiDomain}/sign-in/steam`);
        const state = encodeURIComponent(JSON.stringify({
            actionId: params.actionId,
            giveawayId: params.giveawayId,
            redirectURL: `${apiGatewayEndpoint}${giveawayEndpointIds.CREATE_GIVEAWAY_ENTRY_CALLBACK}`,
            fallbackURL: `${window.location.origin}${routeUrls.authPopupCallback()}`,
            token: getCookie('accessToken'),
            popup: true,
            soft: true,
        }));
        url.searchParams.set('state', state);
        popup.location.href = url.toString();
        const broadcastChannel = getAuthBroadcastChannel();
        return new Promise((resolve, reject) => {
            const checkedClose = setInterval(() => {
                if (popup.closed) {
                    cleanup();
                    reject(new Error('Steam sign-in popup closed'));
                }
            }, 1000);
            const cleanup = () => {
                broadcastChannel.close();
                clearInterval(checkedClose);
                if (!popup.closed)
                    popup.close();
            };
            broadcastChannel.addEventListener('message', (messageEvent) => {
                const { type } = messageEvent.data;
                switch (type) {
                    case AuthBroadcastEvent.AUTH_SUCCESS: {
                        analyticsSendItem(giveawayAnalyticsEvents.SUBMIT_STEAM_ENTRY_SUCCESS, userId);
                        cleanup();
                        resolve();
                        break;
                    }
                    case AuthBroadcastEvent.AUTH_ERROR: {
                        console.error(messageEvent);
                        const { message, code } = messageEvent.data;
                        cleanup();
                        const error = new Error(message !== null && message !== void 0 ? message : 'Steam verification failed');
                        if (code) {
                            error.code = code;
                        }
                        reject(error);
                        break;
                    }
                    default:
                        cleanup();
                        reject(new Error('Unknown message type'));
                        break;
                }
            });
            broadcastChannel.addEventListener('messageerror', (messageEvent) => {
                console.error(messageEvent);
                cleanup();
                reject(new Error('Steam sign-in message error'));
            });
        });
    });
    const enterGiveaway = (params) => __awaiter(void 0, void 0, void 0, function* () {
        analyticsSendItem(giveawayAnalyticsEvents.SUBMIT_ENTER_GIVEAWAY, userId);
        return dispatch(apiRequest(giveawayEndpointIds.ENTER_GIVEAWAY, {
            giveawayId: params.giveawayId,
        }));
    });
    const isGiveawayActionDone = (params) => __awaiter(void 0, void 0, void 0, function* () {
        return dispatch(apiRequest(giveawayEndpointIds.IS_GIVEAWAY_ACTION_DONE, {
            giveawayId: params.giveawayId,
            actionId: params.actionId,
        }));
    });
    const saveGiveawayTerms = () => __awaiter(void 0, void 0, void 0, function* () {
        return dispatch(apiRequest(giveawayEndpointIds.SAVE_GIVEAWAY_TERMS));
    });
    const fetchVouchers = (status) => {
        return dispatch(apiRequest(giveawayEndpointIds.GET_USER_VOUCHERS, {
            userId: targetUserId,
            status,
        }));
    };
    return {
        createEntry,
        redeemVoucher,
        openSteamPopup,
        submitSteamEntry,
        enterGiveaway,
        isGiveawayActionDone,
        saveGiveawayTerms,
        fetchVouchers,
    };
};
